// Save-and-resume for creation flows interrupted by the members-only wall.
//
// When a gate blocks a creation action, the page saves its in-progress state
// here; checkout returns the user to the same URL (success_url carries the
// path), and the page restores the draft on mount — drawings, uploads,
// storylines, selections all survive the trip through Stripe.
//
// One draft at a time (last interruption wins), 24h expiry, best-effort:
// storage errors (private mode, quota from big canvas data URLs) never break
// the gate itself.

const KEY = "pixley_creation_draft_v1";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export function saveCreationDraft(page, data) {
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({ page, data, savedAt: Date.now() }),
    );
  } catch {
    // Quota exceeded (canvas data URLs can be big) — retry without any
    // image-ish fields so at least the text survives.
    try {
      const slim = { ...data };
      for (const k of Object.keys(slim)) {
        if (typeof slim[k] === "string" && slim[k].startsWith("data:")) {
          delete slim[k];
        }
      }
      localStorage.setItem(
        KEY,
        JSON.stringify({ page, data: slim, savedAt: Date.now() }),
      );
    } catch {
      /* storage unavailable — draft is best-effort */
    }
  }
}

// Read AND clear the draft if it belongs to `page` and is fresh.
export function takeCreationDraft(page) {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.page !== page) return null;
    if (Date.now() - (parsed.savedAt || 0) > MAX_AGE_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    localStorage.removeItem(KEY);
    return parsed.data || null;
  } catch {
    return null;
  }
}

// Rebuild a File from a stored data URL (for upload fields).
export async function fileFromDataUrl(dataUrl, filename = "restored.png") {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], filename, { type: blob.type || "image/png" });
}
